import React, { useEffect, useRef, useState } from "react";
import AmountInputField from "../../CommonInputFields/AmountInputField";
import PrimaryButton from "../../CommonButtons/PrimaryButton";
import SvgComponent from "../../SVGComponent";
import CommonToast from "../../CommonToast";
import ICONS from "../../../../assets/Icons";
import { toast } from "react-toastify";

/**
 * AmountRangeDropdown Component
 * @component
 *
 * @param {Object} props - Component props.
 *
 * @param {string} [props.placeholder="Set range"] - Placeholder text shown when no range is applied.
 *
 * @param {boolean} [props.disabled=false] - Disables interaction and prevents opening dropdown.
 *
 * @param {Function} [props.onOpen] - Callback fired when dropdown opens.
 *
 * @param {Function} [props.onClose] - Callback fired when dropdown closes.
 *
 * @param {Function} [props.onApply] - Callback fired when user presses "Apply".
 * Receives: `{ min: string, max: string }`
 *
 * @param {Function} [props.onClear] - Callback fired when user clears both inputs.
 *
 * @param {"small" | "big"} [props.type="small"] - Controls size (affects dropdown height + font).
 *
 * @param {{ min: string | number, max: string | number }} props.range - Current applied range coming from parent.
 *
 * @param {Function} props.setRange - Setter function to update range in the parent state.
 * Called with: `{ min: string, max: string }`
 *
 */

const AmountRangeDropdown = ({
  placeholder = "Set amount range",
  disabled = false,
  onOpen,
  onClose,
  onApply,
  onClear,
  type = "small",
  range,
  setRange,
}) => {
  const [dropdown, setDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const [localMin, setLocalMin] = useState("");
  const [localMax, setLocalMax] = useState("");

  useEffect(() => {
    setLocalMin(range.min ?? "");
    setLocalMax(range.max ?? "");
  }, [range.min, range.max]);

  const toggle = () => {
    if (disabled) return;
    const next = !dropdown;
    setDropdown(next);
    next ? onOpen?.() : onClose?.();
  };

  const handleInput = (name, raw) => {
    const numeric = raw.replace(/[^0-9.]/g, "");

    if (!numeric || isNaN(Number(numeric))) {
      name === "min" ? setLocalMin("") : setLocalMax("");
      return;
    }
    if (!/^\d*\.?\d{0,2}$/.test(numeric)) return;

    name === "min" ? setLocalMin(numeric) : setLocalMax(numeric);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setLocalMin("");
    setLocalMax("");
    setRange({ min: "", max: "" });
    onClear?.();
  };

  const handleApply = (e) => {
    e.stopPropagation();
    if (!localMin || !localMax || Number(localMin) >= Number(localMax)) return;

    setRange({ min: localMin, max: localMax });
    onApply?.({ min: localMin, max: localMax });
    setDropdown(false);
    onClose?.();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdown &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdown(false);
        onClose?.();
      }
    };

    if (dropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdown, onClose]);

  const formatAmount = (value) => {
    if (!value) return "";
    return Number(value).toLocaleString("en-IN");
  };

  const font = type === "small" ? "text-font-xs" : "text-font-md";
  const showApplied = range.min && range.max;
  const isMaxLessThanMin =
    localMin &&
    localMax &&
    !isNaN(Number(localMin)) &&
    !isNaN(Number(localMax)) &&
    Number(localMax) < Number(localMin);

  const handleMaxErrorClick = () => {
    toast.dismiss();
    CommonToast({
      text: "Maximum amount cannot be less than minimum amount.",
      icon: ICONS?.INFO_FILLED,
      variant: "var(--error)",
      buttonNeeded: false,
    });
  };

  const upArrowIcon =
    "https://q2pay.in/new_assets/common/arrows/arrow_up_white.svg";
  const downArrowIcon =
    "https://q2pay.in/new_assets/common/arrows/arrow_down_white.svg";

  return (
    <div
      className="w-full min-w-input-min-width"
      ref={dropdownRef}
    >
      <div
        className={`flex flex-col relative w-full ${dropdown ? "open" : ""} ${
          disabled
            ? "cursor-not-allowed opacity-light-opacity"
            : "cursor-pointer opacity-base-opacity"
        }`}
        onClick={toggle}
      >
        <div
          className={`bg-primary-nav-act ${
            type === "small"
              ? "max-h-dropdown-option-height-sm"
              : "max-h-dropdown-option-height-md"
          } ${!disabled && "global-hover-white"}
            z-z-index-base relative flex items-center justify-between w-full
            px-padding-md py-padding-sm gap-gap-sm dropdown_header
            ${dropdown ? "dropdown_border_bottom_radius_none" : "dropdown_border-radius-md"}`}
        >
          <span
            className={`${font} truncate select-none ${
              showApplied ? "text-text-primary" : "text-text-secondary"
            }`}
          >
            {showApplied
              ? `INR ${formatAmount(range.min)} - INR ${formatAmount(range.max)}`
              : placeholder}
          </span>

          <div className="flex flex-row gap-gap-sm items-center flex-shrink-0">
            <img
              onClick={handleClear}
              className="h-icon-xs w-icon-xs"
              src="https://q2pay.in/new_assets/common/create_flow/close.svg"
              alt="clear"
              style={{ display: range.min && range.max ? "flex" : "none" }}
            />
            <div
              style={{ display: range.min && range.max ? "flex" : "none" }}
              className="border-l max-h-dropdown-divider-height h-screen items-center border-primary-bg"
            />
            {dropdown ? (
              <SvgComponent
                url={upArrowIcon}
                color="var(--text-primary)"
                width="var(--icon-xs)"
                height="var(--icon-xs)"
              />
            ) : (
              <SvgComponent
                url={downArrowIcon}
                color="var(--text-primary)"
                width="var(--icon-xs)"
                height="var(--icon-xs)"
              />
            )}
          </div>
        </div>

        <div className="relative">
          <div
            className="bg-primary-nav-act absolute z-z-index-dropdown overflow-hidden w-full rounded-b-border-radius-md"
            style={{
              maxHeight: dropdown
                ? type === "big"
                  ? "var(--max-animation-height-big-ddl)"
                  : "var(--max-animation-height-small-ddl)"
                : "0px",
              transition: "max-height var(--animation-fast) ease-in-out",
            }}
          >
            <div
              className="border-primary-bg border-t mx-margin-md transition-all duration-[var(--animation-fast)]"
              style={{ opacity: dropdown ? 1 : 0 }}
            />
            <div
              className={`flex flex-col pt-padding-md px-padding-md pb-padding-base gap-gap-sm bg-primary-nav-act w-full rounded-b-border-radius-md overflow-auto border-t-border-base`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col  gap-gap-md">
                <AmountInputField
                  isFullWidth
                  placeholder="Minimum amount"
                  bgColor="var(--primary-nav-act)"
                  value={localMin}
                  onInput={(val) => handleInput("min", val)}
                  fontKind="small"
                  variant="default"
                  fgColor="var(--text-primary)"
                />
                <AmountInputField
                  isFullWidth
                  placeholder="Maximum amount"
                  bgColor="var(--primary-nav-act)"
                  value={localMax}
                  onInput={(val) => handleInput("max", val)}
                  fontKind="small"
                  variant={isMaxLessThanMin ? "error" : "default"}
                  fgColor="var(--text-primary)"
                  adornment={
                    isMaxLessThanMin
                      ? ICONS.ERROR_WITH_WHITE_OUTLINED_CIRCLE
                      : ""
                  }
                  onAdornmentClick={
                    isMaxLessThanMin ? handleMaxErrorClick : undefined
                  }
                />
              </div>

              <span className="text-text-secondary text-font-xxs ml-margin-sm">
                * Specify the amount to filter by.
              </span>

              <div className="flex justify-center gap-gap-sm pb-padding-sm">
                <PrimaryButton
                  btnText="Clear"
                  handleFunction={handleClear}
                  enabledBgColor="var(--primary-nav-act)"
                  outLineColor="var(--text-primary)"
                  enabledTextColor="var(--text-primary)"
                  isDisabled={!localMin && !localMax}
                  fullWidthRequired={true}
                  hoverOption="white"
                />
                <PrimaryButton
                  inActiveVariantNeeded={true}
                  btnText="Apply"
                  handleFunction={handleApply}
                  enabledBgColor="var(--text-primary)"
                  enabledTextColor="var(--primary-bg)"
                  isDisabled={
                    !localMin ||
                    !localMax ||
                    Number(localMin) >= Number(localMax)
                  }
                  disabledBgColor="var(--text-disabled)"
                  disabledTextColor="var(--text-primary)"
                  fullWidthRequired={true}
                  hoverOption="black"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmountRangeDropdown;
